import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import AvatarContainer from "../../../../lib/components/Avatar";
import LoadingSvg from "../../../../lib/components/Icons/LoadingSvg";
import SpinnerIcon from "../../../../lib/components/Icons/SpinnerIcon";

import { journeyStages } from "./stageGraphView";
import { DAY_MS, formatIstDate, istDayDiff } from "./deliveryClock";

/* ─────────────────────────────────────────────────────────────────────────
   Shared presentation primitives for the delivery dashboard.

   Single source of truth shared across FulfillmentTaskCard / KanbanBoard /
   FulfillmentDetailModal / FulfillmentCalendarView: status colours, plan
   badges, avatars, date formatting, progress semantics, dedupe.

   Colour system — ONE meaning per hue (Tailwind v2 AOT: compiled palette
   only — gray/red/yellow/green/blue/indigo/purple/pink; no arbitrary values):
     gray    unclaimed credit (inert)
     yellow  needs an expert (on the fallback), and due-today urgency
     blue    assigned (not started)
     indigo  in progress / the "current" thing
     purple  on hold / paused
     green   done / closed
     red     overdue / cancelled / destructive

   Progress semantics — "closed" means completed OR skipped, matching the
   engine's allCompleted gate (L1 invariant 5a: a closed stage is closed
   however it closed). Every fraction/bar/dot must use closedCount, never a
   completed-only count.
   ───────────────────────────────────────────────────────────────────────── */

/**
 * Why a picker row is offered WITHOUT the service's capability: the server
 * marks the buyer's CRM-routed people (`crmRouted: "ops" | "coach"`) on the
 * card-scoped list, with that one clause waived exactly as the assign /
 * reassign write waives it. One spelling for both pickers.
 */
export const CRM_ROUTED_LABEL = {
  ops: "Lead's ops member",
  coach: "Lead's coach",
};

export const STATUS_TOKENS = {
  unclaimed: {
    label: "Unclaimed credits",
    dot: "bg-gray-400",
    badge: "bg-gray-100 text-gray-600 border border-gray-200",
  },
  /**
   * A card a delivery manager is holding because no assignment strategy
   * matched it. Not a status — a board COLUMN, computed server-side from
   * `assignedVia`. A card always has somebody's name on it, so there is no
   * "unassigned" column for one to sit in.
   */
  fallback: {
    label: "Needs expert",
    dot: "bg-yellow-400",
    badge: "bg-yellow-50 text-yellow-700 border border-yellow-200",
  },
  assigned: {
    label: "Assigned",
    dot: "bg-blue-400",
    badge: "bg-blue-50 text-blue-700 border border-blue-200",
  },
  in_progress: {
    label: "In Progress",
    dot: "bg-indigo-500",
    badge: "bg-indigo-50 text-indigo-700 border border-indigo-200",
  },
  on_hold: {
    label: "On Hold",
    dot: "bg-purple-500",
    badge: "bg-purple-50 text-purple-700 border border-purple-200",
  },
  completed: {
    label: "Completed",
    dot: "bg-green-500",
    badge: "bg-green-50 text-green-700 border border-green-200",
  },
  cancelled: {
    label: "Cancelled",
    dot: "bg-red-400",
    badge: "bg-red-50 text-red-700 border border-red-200",
  },
  /* The plan that granted the unit ran past its validity before the work
     completed (server `plan-grant-expiry` sweep). Dead like `cancelled`; the
     board archives it in the cancelled lens. */
  expired: {
    label: "Expired",
    dot: "bg-gray-400",
    badge: "bg-gray-100 text-gray-700 border border-gray-200",
  },
};

/**
 * "Is this card only on the fallback?" — the client half of the server's
 * `assignedVia` partition.
 *
 * The one predicate every surface asks, so the board column, the Assign action,
 * the failure-trace chip and the calendar tint can never disagree about which
 * cards those are.
 *
 * Prefer `columnOf(item)` where you have the whole item: the server ships the
 * answer and this is only the fallback for an item that predates it.
 */
/**
 * The statuses the board's columns draw from — the client's copy of the
 * server's `COLUMN_LIVE_STATUSES`. A stub buckets by ownership like any live
 * card: ownerless or fallback-held lands in Needs-expert, owner-held in
 * Assigned. Terminal and on_hold cards keep their own column.
 */
const COLUMN_LIVE_STATUSES = [
  "unassigned",
  "assigned",
  "in_progress",
  "unclaimed",
];

export function isFallbackCard(fulfillment) {
  if (!fulfillment) return false;
  if (!COLUMN_LIVE_STATUSES.includes(fulfillment.fulfillmentStatus)) {
    return false;
  }
  return (
    fulfillment.assignedVia === "manager_fallback" ||
    // The transient birth status: the fallback assign never landed, so nobody
    // holds this at all. Strictly worse than a fallback, and it belongs in the
    // same column an operator is already watching.
    fulfillment.fulfillmentStatus === "unassigned" ||
    // NOBODY HOLDS IT, whatever the status says. A card does not stay in the
    // status it was stranded in — starting a stage moves it to `in_progress`
    // whether or not anyone owns it — so a card held in the ops-grace window
    // that somebody then works used to leave this column at the moment it
    // became stranded, and started looking like ordinary in-flight work.
    // Ownerless IS the triage signal; how far the work has got is another axis.
    !fulfillment.assignedProviderId
  );
}

/**
 * Which board COLUMN a card belongs in — the client's copy of the server's
 * `BOARD_COLUMN_EXPR`.
 *
 * A partition over the status, not a second axis: a fallback card's
 * `fulfillmentStatus` IS "assigned", so anything that buckets by status alone
 * silently drops it into the wrong column. Every client-side re-bucketing goes
 * through here so the optimistic view and the server's facets agree.
 */
export function boardColumnOf(fulfillment) {
  if (isFallbackCard(fulfillment)) return "fallback";
  // An owner-held stub rides the Assigned column — it just has no pipeline yet.
  if (fulfillment?.fulfillmentStatus === "unclaimed") return "assigned";
  return fulfillment?.fulfillmentStatus;
}

/**
 * Which board COLUMN an ITEM belongs in — ask this, not `boardColumnOf`.
 *
 * The server computes `_column` on every pipeline that feeds this client and
 * now ships it on the item, so this is a READ of the answer rather than a
 * second implementation of the rule. That matters twice over:
 *
 *  - Five surfaces used to each restate the partition and three of them got it
 *    wrong — a permanently empty Needs-expert column on the mini board, an
 *    ownerless card missing from the delivery panel's own summary, and an
 *    Assign button that could never appear.
 *  - The column is AUDIENCE-RELATIVE. A provider holding one stage of a
 *    fallback-held card has no Needs-expert column, so the server reports that
 *    card to them as `assigned` — and only the server knows who is asking.
 *    Re-deriving it here moved the card into a column they never render, where
 *    it vanished until a refresh put it back.
 *
 * The derivation stays as the fallback for an item from an older cache.
 */
export function columnOf(item) {
  return item?._column ?? boardColumnOf(item?.fulfillment);
}

export function StatusBadge({ status }) {
  // An unrecognised status falls back to `assigned`: every card has an owner,
  // so that is the safest thing to claim about one we cannot name.
  const t = STATUS_TOKENS[status] || STATUS_TOKENS.assigned;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${t.badge}`}>
      {t.label}
    </span>
  );
}

/* Plan badge — deliberately NEUTRAL for every tier. The tier name carries the
   information; a coloured badge would collide with the status hues and become
   the loudest element on the card. */
export function PlanBadge({ plan, small = false }) {
  if (!plan) return null;
  return (
    <span
      className={`inline-flex items-center rounded border border-gray-200 bg-gray-100 text-gray-500 flex-shrink-0 font-medium leading-none ${
        small ? "px-1 py-0.5" : "px-1.5 py-0.5"
      }`}
      style={{ fontSize: 11 }}
    >
      {plan}
    </span>
  );
}

/* ── Urgency ──
   The only red/amber element allowed on a card. Pure helper exported for
   tests and for callers that only need the classification.

   The day is an IST calendar day, not the browser's — ops speaks IST, and the
   server's urgency lens, the triage-rail rollups, the calendar's dots and the
   daily reminder sweep all classify on the IST day. Classifying on the
   operator's local zone would count a card red-overdue on the rail yet label
   it yellow "Due today" on the chip beside it. */

/**
 * Is this card finished with — completed, cancelled or expired?
 *
 * ONE definition, because the alternative is what shipped: cancelling leaves
 * `stages` untouched, so a dead card keeps stages that are `pending`, dated in
 * the past and assigned to somebody. Every predicate that asked the STAGE
 * instead of the CARD kept answering "open" on work nobody will ever do — a
 * red "Overdue" on a killed task, and a Reassign button that reached a live
 * API. Anything gating on "is there still work here?" asks this, not the stage.
 *
 * The server's half of the same rule is `NON_TERMINAL_FULFILLMENT_STATUSES`.
 */
export const CLOSED_CARD_STATUSES = ["completed", "cancelled", "expired"];

export function isClosedCardStatus(status) {
  return CLOSED_CARD_STATUSES.includes(status);
}

export function urgencyInfo(dueDate, status, now = new Date()) {
  if (!dueDate) return null;
  /* `unclaimed` is deliberately NOT excluded here: a credit's delivery promise
     runs from purchase, so an unspent one is the work most likely to go quietly
     late, and the server's urgency lens counts it too. Only completed or
     cancelled cards genuinely cannot be late. */
  if (isClosedCardStatus(status)) return null;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return null;
  const dayDiff = istDayDiff(due, now);
  if (dayDiff === null) return null;
  if (dayDiff < 0) {
    return { kind: "overdue", label: `Overdue ${-dayDiff}d` };
  }
  if (dayDiff === 0) {
    // Same calendar day. Past the timestamp already? Still "today" — ops
    // language treats the day, not the minute, as the unit.
    return { kind: "today", label: "Due today" };
  }
  if (dayDiff === 1) return { kind: "upcoming", label: "Due tomorrow" };
  return { kind: "upcoming", label: `Due ${formatDate(due, { withYear: false })}` };
}

const URGENCY_STYLES = {
  overdue: "bg-red-50 text-red-600 border border-red-200 font-medium",
  today: "bg-yellow-50 text-yellow-700 border border-yellow-200 font-medium",
  upcoming: "text-gray-400",
};

export function UrgencyChip({ dueDate, status, className = "" }) {
  const info = urgencyInfo(dueDate, status);
  if (!info) return null;
  return (
    <span
      className={`inline-flex items-center flex-shrink-0 gap-1 px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${URGENCY_STYLES[info.kind]} ${className}`}
      title={`Due ${formatDate(dueDate)}`}
    >
      {info.kind === "overdue" && (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )}
      {info.label}
    </span>
  );
}

/* ── Awaiting delivery start (frozen SLA) ──
   A plan-granted card whose SLA clock hasn't started yet. Deliberately NEUTRAL
   (gray, no urgency hue) — it isn't late, it simply hasn't begun. Stands in for
   the due chip, which is empty anyway: a frozen card carries no due date. The
   pause glyph reads "clock stopped". */
/* A frozen card's SLA clock hasn't started. Two flavours that matter
 * operationally:
 *   - no gate  → "Awaiting start": somebody just has to click Start.
 *   - blocked  → waiting on OTHER work, named in `unmet`.
 * Without the blocked distinction, a plan-gated card looks like one an operator
 * forgot — an automated flow silently becomes an outage. The unmet conditions
 * are server-rendered labels; never re-derive them from slugs here. */
export function PendingStartChip({
  className = "",
  blocked = false,
  unmet = [],
  // The server couldn't evaluate the gate (the annotation query failed). Say
  // "couldn't check" rather than the plain Awaiting-start chip, which would
  // assert "not blocked".
  unknown = false,
}) {
  const labels = (unmet || []).map((c) => c?.label).filter(Boolean);
  if (!blocked && unknown) {
    return (
      <span
        className={`inline-flex items-center flex-shrink-0 gap-1 px-1.5 py-0.5 rounded text-xs whitespace-nowrap bg-gray-100 text-gray-500 border border-gray-200 ${className}`}
        title="Delivery not started. We couldn't check whether a plan delivery gate is holding this one — retry in a moment."
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Awaiting start
      </span>
    );
  }
  if (blocked) {
    return (
      <span
        className={`inline-flex items-center flex-shrink-0 gap-1 px-1.5 py-0.5 rounded text-xs whitespace-nowrap bg-yellow-50 text-yellow-700 border border-yellow-200 ${className}`}
        title={
          labels.length
            ? `Blocked — delivery starts once ${labels.join(", ")}`
            : "Blocked — waiting on another service in this plan"
        }
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Blocked
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center flex-shrink-0 gap-1 px-1.5 py-0.5 rounded text-xs whitespace-nowrap bg-gray-100 text-gray-500 border border-gray-200 ${className}`}
      title="Delivery not started — the SLA clock is frozen until it begins"
    >
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      Awaiting start
    </span>
  );
}

/**
 * Roll-up flags for a COLLAPSED service group in the user lenses.
 *
 * Grouping a user's cards by service hides the cards themselves until the group
 * is opened, which hides the two states an operator must never have to go
 * looking for: a card nothing can start (gate-blocked) and a card already late.
 * A group holding either looked identical to one holding neither, so the only
 * way to find them was to open every group on the page.
 *
 * Icon-only, and deliberately so: the label row is already carrying a name, a
 * count and a chevron, and a "Blocked"/"Overdue" word each would push the
 * service name into truncation on exactly the rows that most need reading. The
 * colours are the ones these two states already own everywhere else — the
 * padlock is `PendingStartChip`'s yellow, the clock is `UrgencyChip`'s red — so
 * the icon reads as the same fact the card will show once opened, not a new one.
 *
 * ANY card in the group raises the flag: this is a "look in here" marker, not a
 * count, and one blocked card among ten is exactly the case a count would bury.
 */
export function ServiceGroupFlags({
  items = [],
  className = "",
  // Some group headers already state "N overdue" in words on the same row. The
  // clock there would be the same fact twice, so those callers turn it off and
  // keep the flag that has no textual twin.
  showOverdue = true,
}) {
  const blocked = items.some((i) => !!i?.gateBlocked);
  const overdue = showOverdue && items.some(
    (i) =>
      urgencyInfo(i?.fulfillment?.dueDate, i?.fulfillment?.fulfillmentStatus)
        ?.kind === "overdue"
  );
  if (!blocked && !overdue) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 flex-shrink-0 ${className}`}
      data-testid="service-group-flags"
    >
      {blocked && (
        <svg
          className="w-3 h-3 text-yellow-700"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          role="img"
          aria-label="Blocked"
          data-testid="group-flag-blocked"
        >
          <title>At least one card here is blocked — waiting on other work in the plan</title>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )}
      {overdue && (
        <svg
          className="w-3 h-3 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          role="img"
          aria-label="Overdue"
          data-testid="group-flag-overdue"
        >
          <title>At least one card here is overdue</title>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )}
    </span>
  );
}

/* ── Stage access ── */

/** True when `stage` is assigned to `currentUserId`. */
export function stageAssigneeIsYou(stage, currentUserId) {
  const assigneeId =
    stage?.assigneeId?.toString?.() || stage?.assigneeId || null;
  return !!(
    assigneeId &&
    currentUserId &&
    assigneeId.toString() === currentUserId.toString()
  );
}

/**
 * May this viewer use the STAFF stage controls — start, skip, complete, set its
 * dates, reassign (`StageActionBar`, the card's advance button)?
 *
 * A read-only viewer NEVER can, not even on a stage assigned to them. The
 * customer acts on their own stages through a different, narrower door: the
 * flags the server ships on each of those stages (`customerStageActions`) drawn
 * by `CustomerStageActions` — Start / Mark as done / Skip and nothing else, no
 * dates, no reassign, no other stage. Keep the two apart: widening this gate to
 * let a customer through would hand them the whole staff bar. Otherwise
 * oversight acts on any stage and an assignee on their own. The server enforces
 * the same rule; this only decides what to draw.
 *
 * `viewOnly` is the other way to be shown a card without being handed it: the
 * lead view (see `isViewOnly`). It is staff looking at a card that is not
 * theirs, so unlike `readOnly` it hides NO content — and like it, no control.
 */
export function viewerCanActOnStage({ readOnly, viewOnly, isOversight, isYou }) {
  return !readOnly && !viewOnly && (!!isOversight || !!isYou);
}

/**
 * What the server lets THIS customer do on a stage of their own —
 * `{ start, complete, skip, note }`, or null.
 *
 * The flags are the server's single predicate, the one its `my-stages` write
 * checks, shipped only on the viewer's own CUSTOMER stages; the client draws
 * exactly what they say and never works out eligibility itself. The role and
 * assignee test in front of them is not a second opinion — it is the product
 * rule ("only stages assigned to you") restated as a fence, so a payload that
 * ever mis-projects the flags onto somebody else's stage can only hide a
 * control, never put one there.
 */
export function customerStageActions(stage, currentUserId) {
  if (stage?.assigneeRole !== "CUSTOMER") return null;
  if (!stageAssigneeIsYou(stage, currentUserId)) return null;
  return stage.customerActions || null;
}

/**
 * Is this stage waiting on the customer looking at it — may they Start, finish
 * or Skip it right now? The "Your step" / "Pending on you" question. A note
 * alone is not a step to take, so `note` does not count.
 */
export function hasCustomerAction(stage, currentUserId) {
  const a = customerStageActions(stage, currentUserId);
  return !!(a && (a.start || a.complete || a.skip));
}

/* ── The lead view ──
   An ops member is answerable for a lead's delivery, and hands parts of it to
   other experts. They are shown every card of that lead — and may act only on
   the ones that are theirs. WHICH cards are look-only is the server's answer
   (`viewOnly` on the item, shipped like `_column`); nothing here works it out. */

/** Is this card one the viewer may look at but not act on? */
export function isViewOnly(item) {
  return item?.viewOnly === true;
}

const EYE_PATH =
  "M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z";

function EyeIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d={EYE_PATH} />
    </svg>
  );
}

/**
 * The tile's marker: that the card is look-only, and WHO holds it — the one
 * fact an ops member scanning their lead's work is looking for. Neutral grey on
 * purpose: nothing is wrong with the card, it is simply not theirs.
 */
export function ViewOnlyChip({ assigneeName, className = "" }) {
  const holder = assigneeName?.trim() || "No owner";
  return (
    <span
      data-testid="view-only-chip"
      title={`You can see this task because you are this student's ops. It is ${
        assigneeName?.trim() ? `assigned to ${holder}` : "not assigned to anyone"
      } — open it to follow progress.`}
      className={`inline-flex items-center max-w-full px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 ${className}`}
    >
      <EyeIcon className="w-3 h-3 mr-1 flex-shrink-0" />
      <span className="truncate">View only · {holder}</span>
    </span>
  );
}

/**
 * The modal's banner for a look-only card. Says the two things the viewer
 * would otherwise have to infer from missing buttons: who has the work, and
 * why they are being shown it.
 */
export function ViewOnlyBanner({ assigneeName, studentName }) {
  const holder = assigneeName?.trim();
  const student = studentName?.trim() || "this student";
  return (
    <div
      data-testid="view-only-banner"
      className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3"
    >
      <EyeIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 leading-snug">
          {holder
            ? `View only — assigned to ${holder}.`
            : "View only — not assigned to anyone yet."}
        </p>
        <p className="mt-0.5 text-xs text-gray-600 leading-snug">
          You can see this because you are {student}&rsquo;s ops. You can follow
          its progress here; changes are made by whoever holds the task.
        </p>
      </div>
    </div>
  );
}

/**
 * May this actor OPEN a stage's details?
 *
 * Deliberately NOT `canEdit`. Opening a stage is a READ — its notes, dates,
 * assignee and signal history — and gating a read on write permission makes a
 * COMPLETED card impossible to inspect (a closed card sets `canEdit` false to
 * hide the action buttons). The card's status is therefore absent from this
 * predicate on purpose; it belongs only to `stagePermissions`, which governs
 * what you may DO.
 */
export function canInspectStage(stage, { isOversight, currentUserId } = {}) {
  if (!stage) return false;
  return !!isOversight || stageAssigneeIsYou(stage, currentUserId);
}

/* ── Progress semantics ── */
export function closedCount(stages = []) {
  return stages.filter((s) => s.status === "completed" || s.status === "skipped").length;
}

/* Stage dots — the pipeline's shape at a glance, no arithmetic required.
   done=filled green · skipped=filled gray · current=ringed indigo ·
   pending=hollow. Long pipelines (>10) collapse to a slim segmented bar
   with the same 4-state encoding. */
export function StageDots({ stages = [], className = "" }) {
  // Branch-skipped stages are the path NOT taken — they aren't skipped work,
  // so they get no dot (journey semantics, same as the customer widget), and
  // the dots stay consistent with the "x/y done" fraction beside them.
  const visible = journeyStages(stages);
  if (!visible.length) return null;
  const stateOf = (s) =>
    s.status === "completed" ? "done"
    : s.status === "skipped" ? "skipped"
    : s.status === "in_progress" ? "current"
    : "pending";

  if (visible.length > 10) {
    const SEGMENT = {
      done: "bg-green-500",
      skipped: "bg-gray-300",
      current: "bg-indigo-500",
      pending: "bg-gray-100",
    };
    return (
      <div className={`flex h-1.5 rounded-full overflow-hidden gap-px ${className}`}>
        {visible.map((s, i) => (
          <div
            key={s.stageKey || i}
            className={`flex-1 ${SEGMENT[stateOf(s)]}`}
            title={`${s.stageName || s.stageKey} — ${s.status}`}
          />
        ))}
      </div>
    );
  }

  const DOT = {
    done: "bg-green-500 border border-green-500",
    skipped: "bg-gray-300 border border-gray-300",
    current: "bg-white border-2 border-indigo-500",
    pending: "bg-white border border-gray-300",
  };
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {visible.map((s, i) => (
        <span
          key={s.stageKey || i}
          className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${DOT[stateOf(s)]}`}
          title={`${s.stageName || s.stageKey} — ${s.status}`}
        />
      ))}
    </div>
  );
}

/* ── Dates — ONE locale AND one clock for the whole dashboard ──
   Rendered on the IST calendar, like every other date on this dashboard, so a
   chip's tooltip names the same day as the calendar cell the card sits in. */
export function formatDate(date, { withYear = true } = {}) {
  if (!date) return "";
  return formatIstDate(date, {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

/* Coarse "time since" (e.g. stub minted at purchase). Returns "11h" / "3d"
   or null; callers append " ago". */
export function timeAgo(dateStr, now = Date.now()) {
  if (!dateStr) return null;
  const diff = now - new Date(dateStr).getTime();
  if (Number.isNaN(diff) || diff < 0) return null;
  const hour = 60 * 60 * 1000;
  if (diff < DAY_MS) return `${Math.max(1, Math.floor(diff / hour))}h`;
  return `${Math.floor(diff / DAY_MS)}d`;
}

/* "just now" / "12m ago" / "3h ago" / "2d ago", then a plain date. Finer than
   `timeAgo` because it labels something a PERSON wrote — a comment, a note —
   where "1h ago" for a note left a minute ago reads as stale. The activity
   timeline and the customer's stage note share it, so one card never states the
   age of two writings two ways. */
export function formatRelativeTime(ts, now = new Date()) {
  if (!ts) return "";
  const d = new Date(ts);
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

/* Ageing tone for "sitting untouched" timestamps (unclaimed stubs shouldn't
   rot invisibly): quiet < 7d, warn < 14d, alert after. */
export function ageTone(dateStr, now = Date.now()) {
  if (!dateStr) return "text-gray-400";
  const days = (now - new Date(dateStr).getTime()) / DAY_MS;
  if (Number.isNaN(days)) return "text-gray-400";
  if (days >= 14) return "text-red-500 font-medium";
  if (days >= 7) return "text-yellow-600 font-medium";
  return "text-gray-400";
}

/* ── Dedupe a page by fulfillmentId ──
   The server-side aggregate can race briefly during transitions, and
   paginated pages can overlap; a duplicate React key explodes. */
export function dedupeByFulfillmentId(items) {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const id = item?.fulfillment?.fulfillmentId;
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(item);
  }
  return out;
}

/* ── Paging a list that mutates under you (PG-01) ──────────────────────────
   Every paged surface here reads by OFFSET and patches its own rows when a
   card changes, which invalidates the offset it pages with next: complete a
   card in a 15-of-40 column and the server's set shifts up one, so the next
   `$skip: 15` jumps clean over whatever moved into position 15 — the row is
   unreachable until a reload, with no UI cue.

   So a list whose MEMBERSHIP changed is `dirty`, and a dirty list re-reads
   pages 1..N+1 in ONE request and replaces, instead of skipping past them. An
   in-place row replacement is never dirty — the row changed, the set didn't.

   Lives here (not the redux duck) because the board columns and the calendar's
   local lists both obey it, and two copies of a pagination rule drift apart.

   The real fix is keyset pagination (needs a server change). `MAX_REFETCH_PAGES`
   bounds the re-read so a deep list falls back to a plain append past that
   depth rather than asking for an unbounded `limit`. Keep it at or below any
   server-side `@Max` on `limit` divided by the page size. */
export const MAX_REFETCH_PAGES = 10;

export function nextChunk({ page = 1, dirty = false }, append, pageSize) {
  const target = append ? (page || 1) + 1 : 1;
  const rereading = append && dirty && target <= MAX_REFETCH_PAGES;
  return rereading
    ? { page: 1, limit: target * pageSize, append: false, resultPage: target }
    : { page: target, limit: pageSize, append, resultPage: target };
}

/* ── Avatar ──
   Wraps the app-wide AvatarContainer so a failed image load degrades to
   coloured initials, never a broken-image glyph. AOT Tailwind needs literal
   dimension classes — never `w-${n}`. */
const AVATAR_DIMS = {
  5: "w-5 h-5",
  6: "w-6 h-6",
  7: "w-7 h-7",
  8: "w-8 h-8",
  9: "w-9 h-9",
};

export function Avatar({ name, src, size = 7, className = "" }) {
  return (
    <div className={`flex-shrink-0 ${className}`}>
      <AvatarContainer
        url={src}
        fallbackName={name}
        dimensions={AVATAR_DIMS[size] || AVATAR_DIMS[7]}
      />
    </div>
  );
}

/* Reactive mobile check — matches the board's `md:` breakpoint (768px), the
   same boundary the calendar view and the Users-lens tap behavior use. The
   implementation is the platform's one viewport hook; re-exported here so the
   board's imports keep working. */
export { default as useIsMobileViewport } from "../../../common/useIsMobileViewport";

export function Chevron({ open, className = "" }) {
  return (
    <svg
      className={`w-3 h-3 text-gray-300 flex-shrink-0 transition-transform duration-150 ${open ? "transform rotate-90" : ""} ${className}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

/* ── ConfirmAction ──
   In-context two-step confirm for destructive actions — replaces
   window.confirm (jarring, blocks the tab, unstyled). Renders the trigger
   button; clicking it opens a small anchored popover with the consequence
   message and an explicit destructive confirm. */
/** The restart confirm's tone: primary, never red — a restart loses nothing. */
export const RESTART_CONFIRM_CLASS =
  "text-xs px-2.5 py-1 rounded-md bg-primary text-white hover:bg-jove-200 font-medium transition-colors";

export function ConfirmAction({
  label,
  message,
  confirmLabel = "Confirm",
  onConfirm,
  disabled = false,
  testId,
  /**
   * Opt-in free-text field. When set, the popover renders a textarea and
   * `onConfirm` receives its trimmed value (or undefined when left blank) —
   * so an optional explanation is captured inside the confirmation that
   * already exists, instead of stacking a second dialog on top of it.
   */
  inputLabel,
  inputPlaceholder = "",
  buttonClassName = "text-xs px-2 py-1 text-gray-400 hover:text-red-500 rounded font-medium transition-colors",
  /**
   * The confirm button's classes. Red by default — most confirmations here
   * are destructive — but a RESTART is not (nothing the buyer paid for is
   * lost), so it passes the primary tone instead. Red is reserved for revoke.
   */
  confirmClassName = "text-xs px-2.5 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 font-medium transition-colors",
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [placement, setPlacement] = useState("down");
  const [align, setAlign] = useState("right");
  const rootRef = useRef(null);
  const popRef = useRef(null);

  /**
   * Flip upward when opening downward would push the popover past the bottom of
   * whatever clips it. `position: fixed` is NOT an option here — the delivery
   * modal sets `transform`, which makes any transformed ancestor the containing
   * block, so a fixed popover lands somewhere else entirely (01-delivery-
   * dashboard decision 17). Staying absolute and flipping is what works.
   *
   * The boundary is the nearest SCROLLING ancestor, falling back to the
   * viewport: the scroller is what will actually clip the popover, as on the
   * stage timeline (a 60vh scroll list with the inspector docked inside it).
   */
  useLayoutEffect(() => {
    if (!open) {
      setPlacement("down");
      setAlign("right");
      return;
    }
    const trigger = rootRef.current;
    const pop = popRef.current;
    if (!trigger || !pop) return;

    // The nearest ancestor that will actually cut the popover off. A scroller
    // clips BOTH axes even when only one of them scrolls: per the overflow
    // spec a `visible` axis paired with a non-visible one computes to `auto`,
    // which is why the modal body (`overflow-y-auto`) also clips horizontally.
    let bounds = {
      bottom: window.innerHeight || 0,
      left: 0,
      right: window.innerWidth || 0,
    };
    for (let p = trigger.parentElement; p; p = p.parentElement) {
      const clipsY = p.clientHeight > 0 && p.scrollHeight > p.clientHeight;
      const clipsX = p.clientWidth > 0 && p.scrollWidth > p.clientWidth;
      if (clipsY || clipsX) {
        bounds = p.getBoundingClientRect();
        break;
      }
    }

    const rect = trigger.getBoundingClientRect();
    const popHeight = pop.offsetHeight || pop.getBoundingClientRect().height || 0;
    const popWidth = pop.offsetWidth || pop.getBoundingClientRect().width || 0;
    setPlacement(rect.bottom + 4 + popHeight > bounds.bottom ? "up" : "down");
    // `right-0` hangs the popover LEFTWARD off the trigger; near the clipper's
    // left edge that spills outside and gets cut in half, so pin the other edge
    // instead. `position: fixed` can't escape the clipper here — the modal's
    // `transform` makes fixed resolve against the modal box (decision 17).
    setAlign(rect.right - popWidth < bounds.left ? "left" : "right");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // A dismissed draft must never ride the next confirmation — reopening starts
  // blank, whether the popover was closed by Keep or by clicking away.
  useEffect(() => {
    if (!open) setValue("");
  }, [open]);

  return (
    <div className="relative inline-block" ref={rootRef} data-action-btn>
      <button
        type="button"
        data-action-btn
        data-testid={testId}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={buttonClassName}
      >
        {label}
      </button>
      {open && (
        <div
          ref={popRef}
          data-confirm-popover
          className={`absolute ${align === "left" ? "left-0" : "right-0"} ${
            placement === "up" ? "bottom-full mb-1" : "top-full mt-1"
          } z-30 ${
            inputLabel ? "w-72" : "w-56"
          } whitespace-normal bg-white border border-gray-200 rounded-lg shadow-lg p-3`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* `whitespace-normal` on the popover and `break-words` here are load-
              bearing, not belt-and-braces: `white-space` INHERITS, and the
              natural home for a row action is a `whitespace-nowrap` table cell.
              Without them the consequence text refuses to wrap and runs clean
              out of the popover across the whole page — the confirm renders,
              but unreadably, on exactly the destructive action that most needs
              reading. `break-words` covers a long unbroken name or slug. */}
          <p className="text-xs text-gray-600 mb-2 break-words">{message}</p>
          {inputLabel && (
            <label className="block mb-2">
              <span className="block text-xs text-gray-500 mb-1">
                {inputLabel}
              </span>
              <textarea
                rows={2}
                value={value}
                autoFocus
                placeholder={inputPlaceholder}
                data-testid={testId ? `${testId}-reason` : undefined}
                onChange={(e) => setValue(e.target.value)}
                className="w-full text-xs p-1.5 border border-gray-200 rounded-md text-gray-700 resize-none"
              />
            </label>
          )}
          <div className="flex items-center justify-end space-x-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="text-xs px-2.5 py-1 rounded-md text-gray-500 hover:bg-gray-100 font-medium transition-colors"
            >
              Keep
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onConfirm?.(inputLabel ? value.trim() || undefined : undefined);
              }}
              className={confirmClassName}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Restarting a card ──
   The statuses `restartFulfillment` refuses: a closed card has nothing left to
   restart, and an `unclaimed` stub has no work to restart — a stub IS the
   credit's own row, and its only door is revoking the credit. */
const UNRESTARTABLE_STATUSES = [...CLOSED_CARD_STATUSES, "unclaimed"];

/* ── Revoking a credit ──
   `revokeFulfillmentCredit` refuses only a closed card (a completed one already
   spent its credit; a cancelled one has nothing left to take). A stub IS
   revocable: the row is pulled and the unit withdrawn. */
const UNREVOKABLE_STATUSES = CLOSED_CARD_STATUSES;

/* ── How a card died ──
   One token per `cancelledVia` value the schema admits, plus `unknown` for
   rows written before the enum existed. Lives HERE, beside the cancel
   affordance, so the archive's rows/chips and the detail modal's banner word a
   cancellation the same way rather than from two copies of these labels.

   `unknown` is a real bucket, not a fallback to hide: the archive's reason
   chips have to sum to its headline total or one of the two is lying.

   `creditNote` is per-reason and MUST stay that way. "Cancelling consumes
   nothing" is true of every path — no cancel writer calls `consumeService` —
   but what happens to the UNIT afterwards differs, so a single outcome for all
   of them would be a false record. There are THREE states, not two:
     consumed  — spent on delivery. No cancellation ever produces this.
     kept      — `restartFulfillment` (restart / release) leaves `remaining`
                 untouched AND mints a replacement in the same write: a fresh
                 card (at_purchase) or a fresh unclaimed stub (at_claim). The
                 legacy `admin` door and `seed-rollback` kept the credit with
                 no replacement.
     revoked   — `EntitlementService.revokeServiceQuota` (the dashboard's
                 Cancel & revoke credit, or a plan-upgrade) `$inc`s
                 `remaining: -1` and `totalPurchased: -1` in the SAME write as
                 the cancel. Not consumed, but not the buyer's any more either.
                 `refunded` and `subscription_cancelled` are the same outcome
                 written by `OrderCompensationService` — the order-level
                 un-grant — and differ only in WHY. */
export const CANCEL_REASON_TOKENS = {
  restart: {
    label: "Restarted",
    chip: "bg-indigo-50 text-indigo-700 border-indigo-200",
    dot: "bg-indigo-500",
    hint: "An operator restarted this task — this card was closed and a fresh one was created for the same credit.",
    creditNote:
      "No credit was consumed — the purchased unit stayed with the buyer, and a fresh card was minted for it.",
  },
  revoked: {
    label: "Credit revoked",
    chip: "bg-red-50 text-red-700 border-red-200",
    dot: "bg-red-500",
    hint: "An operator cancelled this task and withdrew the buyer's credit behind it.",
    creditNote:
      "No credit was consumed, but the unit was WITHDRAWN — it is no longer in the buyer's balance.",
  },
  admin: {
    label: "Cancelled by ops",
    chip: "bg-gray-100 text-gray-700 border-gray-200",
    dot: "bg-gray-500",
    hint: "An operator stopped this task from the delivery dashboard (legacy door — no replacement was minted).",
    creditNote:
      "No credit was consumed — the purchased unit stayed with the buyer.",
  },
  release: {
    label: "Credit released",
    chip: "bg-blue-50 text-blue-700 border-blue-200",
    dot: "bg-blue-500",
    hint: "The credit was released back to the pool — a fresh unclaimed credit was minted for the buyer.",
    creditNote:
      "No credit was consumed — the unit went back to the buyer as an unclaimed credit.",
  },
  refunded: {
    label: "Refunded",
    chip: "bg-red-50 text-red-700 border-red-200",
    dot: "bg-red-500",
    hint: "The order this unit came from was refunded — every unfinished task on it was cancelled and its credit withdrawn.",
    creditNote:
      "No credit was consumed, but the unit was WITHDRAWN with the refund — it is no longer in the buyer's balance.",
  },
  subscription_cancelled: {
    label: "Subscription cancelled",
    chip: "bg-orange-50 text-orange-700 border-orange-200",
    dot: "bg-orange-500",
    hint: "The subscription this unit came from was cancelled — every unfinished task on it was cancelled and its credit withdrawn.",
    creditNote:
      "No credit was consumed, but the unit was WITHDRAWN when the subscription was cancelled — it is no longer in the buyer's balance.",
  },
  "plan-upgrade": {
    label: "Plan changed",
    chip: "bg-purple-50 text-purple-700 border-purple-200",
    dot: "bg-purple-500",
    hint: "A plan change withdrew this unit — the new plan no longer includes it.",
    creditNote:
      "No credit was consumed, but the unit was WITHDRAWN with the plan change — it is no longer in the buyer's balance.",
  },
  plan_expired: {
    label: "Expired",
    chip: "bg-gray-100 text-gray-700 border-gray-200",
    dot: "bg-gray-500",
    hint: "The plan this unit came from passed its validity before the work was completed — the daily expiry sweep closed the task and withdrew its credit.",
    creditNote:
      "No credit was consumed, but the unit was WITHDRAWN when the plan's validity ended — it is no longer in the buyer's balance.",
  },
  "seed-rollback": {
    label: "System rollback",
    chip: "bg-gray-100 text-gray-600 border-gray-200",
    dot: "bg-gray-400",
    hint: "Automated cleanup after a failed card creation. Nobody stopped this work.",
    creditNote:
      "No credit was consumed — the purchased unit stayed with the buyer.",
  },
  unknown: {
    label: "Unrecorded",
    chip: "bg-gray-100 text-gray-500 border-gray-200",
    dot: "bg-gray-300",
    hint: "Cancelled before the reason was recorded.",
    // The reason is what decides kept-vs-revoked, so without one the only
    // honest answer is to say what IS known and not guess the rest.
    creditNote:
      "No credit was consumed. Whether the unit stayed with the buyer or was withdrawn wasn't recorded.",
  },
};

export const CANCEL_REASON_KEYS = [
  "restart",
  "revoked",
  "refunded",
  "subscription_cancelled",
  "plan_expired",
  "release",
  "admin",
  "plan-upgrade",
  "seed-rollback",
  "unknown",
];

/* The vias under which the UNIT LEFT with the card: `remaining` was
   decremented in the same write, so there is no credit behind the dead card
   to put back. Client twin of the server's `CREDIT_WITHDRAWN_VIAS`. */
export const CREDIT_WITHDRAWN_VIAS = [
  "revoked",
  "refunded",
  "subscription_cancelled",
  "plan_expired",
];

export function isCreditWithdrawnVia(via) {
  return CREDIT_WITHDRAWN_VIAS.includes(via);
}

export function cancelReasonToken(via) {
  return CANCEL_REASON_TOKENS[via] || CANCEL_REASON_TOKENS.unknown;
}

/**
 * The one "cancelled" glyph — a no-entry sign, drawn as an explicit circle
 * plus a diagonal.
 *
 * Deliberately NOT the heroicons `ban` path: that icon is three arc segments
 * whose meaning depends on sweep flags, and dropping one — easy to do and
 * invisible in a diff — silently renders a SEMI-CIRCLE with a line through it.
 * A `<circle>` cannot be half-drawn by a typo.
 *
 * One component so the banner and the board's entry button cannot drift into
 * two different cancel symbols.
 */
export function CancelledIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M5.64 5.64l12.72 12.72" />
    </svg>
  );
}

/**
 * The detail modal's cancelled banner — sibling of the on-hold and completed
 * banners, and the answer to "why is this card dead?".
 *
 * `completedAt` doubles as the cancellation stamp — see the `cancelledBy`
 * block in `entitlement.schema.ts`; there is no separate `cancelledAt`. A
 * missing canceller means no human pressed a button (a plan upgrade withdrawing
 * a unit), which is why the copy says "System" rather than leaving a blank.
 */
export function CancelledBanner({ fulfillment, cancelledByName }) {
  const status = fulfillment?.fulfillmentStatus;
  if (status !== "cancelled" && status !== "expired") return null;
  const expired = status === "expired";
  const token = cancelReasonToken(fulfillment.cancelledVia);
  const when = fulfillment.completedAt;
  const by = cancelledByName || "System";
  return (
    <div className="px-6 py-3 border-t border-red-200 bg-red-50">
      <div className="flex items-start space-x-2.5">
        <CancelledIcon className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-red-800">
            {expired ? "This task expired" : "This task was cancelled"}
            {when ? ` on ${formatDate(when)}` : ""}
          </p>
          <p className="text-xs text-red-700 mt-0.5">
            <span
              className={`inline-block px-1.5 py-0.5 rounded border font-medium mr-1.5 ${token.chip}`}
              title={token.hint}
            >
              {token.label}
            </span>{" "}
            by {by}
          </p>
          {fulfillment.cancelReason && (
            <p className="text-xs text-red-700 mt-1 whitespace-pre-wrap">
              &ldquo;{fulfillment.cancelReason}&rdquo;
            </p>
          )}
          {/* What became of the money — the fact a dead card cannot show, and
              the one an operator most often needs. Read from the REASON,
              because the answer genuinely differs by path (see the three
              states above). */}
          <p className="text-xs text-red-400 mt-1">{token.creditNote}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * May this actor restart this card?
 *
 * Oversight (ADMIN or an active internal manager, resolved server-side) and a
 * card the server will actually accept. Deliberately WIDER than the board rows'
 * quick action (which only offers Restart from the Needs-expert column), so
 * held and in-flight cards still have a restart affordance.
 *
 * Hidden for an `unclaimed` stub: there is no work to restart, and the server
 * 409s. A stub's only door is `canRevokeCredit`.
 *
 * `readOnly` matters because the customer opens this same modal from the
 * entitlements widget.
 */
export function canRestartCard({
  isOversight,
  isAssignee = false,
  readOnly = false,
  status,
} = {}) {
  // Card SCOPE, like the route: oversight, OR the card's own assignee (the
  // server records theirs as `release`). Revoke stays oversight-only.
  if ((!isOversight && !isAssignee) || readOnly || !status) return false;
  return !UNRESTARTABLE_STATUSES.includes(status);
}

/**
 * May this actor cancel this card AND revoke the credit behind it?
 *
 * Same audience as restart — oversight only; the route additionally asserts
 * card AUTHORITY server-side — but a different set of cards: a stub is
 * revocable (the unit is withdrawn, row pulled), a closed card is not.
 */
export function canRevokeCredit({ isOversight, readOnly = false, status } = {}) {
  if (!isOversight || readOnly || !status) return false;
  return !UNREVOKABLE_STATUSES.includes(status);
}

/**
 * "Has work started on this card?" — the client twin of the server's
 * `isUntouched` (inverted): in_progress, on_hold, a deliberately started
 * delivery clock, or any stage off `pending`. Decides whether the revoke modal
 * shows its "work has started — revoke anyway" confirmation. The server
 * enforces the same rule; this only shapes the form.
 */
export function cardHasStarted(fulfillment = {}) {
  const status = fulfillment.fulfillmentStatus;
  if (status === "in_progress" || status === "on_hold") return true;
  if (fulfillment.deliveryStartedAt) return true;
  return (fulfillment.stages || []).some(
    (s) => s?.status && s.status !== "pending"
  );
}

/**
 * The modal's restart row. Sits at the bottom, outside the scrolling body and
 * away from every primary action — not inline in the stage action bar, which
 * exists twice (once per layout) and would mean two copies of the verb.
 *
 * NOT styled as destructive: a restart closes this card and creates a fresh
 * one for the same credit, so nothing the buyer paid for is lost. Red is
 * reserved for the neighbouring revoke, which is the door that takes money.
 *
 * The optional reason matters more here than on a board row: restarting
 * in-flight work stops an expert mid-task, and `FulfillmentListener` renders
 * this text to them verbatim.
 */
export function RestartCardRow({
  status,
  isOversight,
  isAssignee = false,
  readOnly,
  onRestart,
}) {
  if (!canRestartCard({ isOversight, isAssignee, readOnly, status })) {
    return null;
  }
  return (
    <div className="px-4 py-3 md:px-6 border-t border-gray-100 flex items-center justify-between gap-3">
      <p className="text-xs text-gray-400">
        Closes this card and creates a fresh one for the same credit. Nothing
        is consumed or withdrawn.
      </p>
      <ConfirmAction
        label="Restart this task"
        message="Closes this card and creates a fresh one for the same credit. Work done on this card is not carried over. Nothing is consumed or withdrawn."
        confirmLabel="Restart task"
        inputLabel="Reason (optional — the assignee is told)"
        inputPlaceholder="e.g. wrong intake form"
        onConfirm={onRestart}
        testId="restart-card"
        buttonClassName="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
        confirmClassName={RESTART_CONFIRM_CLASS}
      />
    </div>
  );
}

/**
 * The modal's revoke trigger — the ONE red verb. Sits beside the restart row
 * and opens `RevokeCreditModal`, which collects the required reason and the
 * "work has started" confirmation. Shown for any live card, stubs included
 * (a stub's unit can be withdrawn; it just cannot be restarted).
 */
export function RevokeCreditRow({ status, isOversight, readOnly, onOpen }) {
  if (!canRevokeCredit({ isOversight, readOnly, status })) return null;
  return (
    <div className="px-4 py-3 md:px-6 border-t border-gray-100 flex items-center justify-between gap-3">
      <p className="text-xs text-gray-400">
        {status === "unclaimed"
          ? "Withdraws this unclaimed credit from the buyer."
          : "Stops this task and withdraws the purchased unit from the buyer."}
      </p>
      <button
        type="button"
        onClick={onOpen}
        data-testid="revoke-credit"
        className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-colors"
      >
        Cancel &amp; revoke credit
      </button>
    </div>
  );
}

/**
 * May this dead card's credit be put back in the claimable pool?
 *
 * The exact inverse of `canRevokeCredit`, and deliberately its neighbour: revoke
 * takes a paid unit OUT of circulation, this puts one back. Both are money
 * moves on someone else's entitlement, so both are oversight-only.
 *
 * Only for at_claim work. An at_purchase credit whose card was cancelled is
 * already back in circulation the moment it dies — `manualCardAvailability`
 * counts it and "Track existing service" offers it — so there is nothing to
 * return, and a button implying otherwise would suggest the credit was somehow
 * stuck.
 *
 * `seedMode` comes from the card's enriched service context. Absent (an older
 * payload) ⇒ hidden: the server refuses a non-at_claim slug anyway, so showing
 * it would only guarantee a red toast.
 */
export function canReturnCredit({
  isOversight,
  readOnly = false,
  status,
  seedMode,
  cancelledVia,
} = {}) {
  if (!isOversight || readOnly) return false;
  // A card cancelled via `revoked` / `refunded` / `subscription_cancelled`
  // has NO credit behind it any more — the unit left the buyer's balance in
  // the same write — so there is nothing to put back. The server refuses it
  // as a source too.
  if (isCreditWithdrawnVia(cancelledVia)) return false;
  return status === "cancelled" && seedMode === "at_claim";
}

/**
 * The detail modal's return-credit row. Sits where `RestartCardRow` sits — the
 * two are never shown together, because one needs a live card and the other a
 * dead one.
 *
 * Two-step, like every other consequential action here, but NOT phrased as
 * destructive: handing a paid credit back to the buyer is the generous
 * direction, and red would misdescribe it.
 */
export function ReturnCreditRow({
  status,
  seedMode,
  cancelledVia,
  isOversight,
  readOnly,
  onReturn,
}) {
  if (
    !canReturnCredit({ isOversight, readOnly, status, seedMode, cancelledVia })
  ) {
    return null;
  }
  return (
    <div className="px-4 py-3 md:px-6 border-t border-gray-100 flex items-center justify-between gap-3">
      <p className="text-xs text-gray-400">
        Nothing was consumed when this was cancelled — the buyer still owns the
        unit. Returning it makes the credit claimable again.
      </p>
      <ConfirmAction
        label="Return credit to pool"
        message="Puts this unit back in the buyer's claimable pool as a fresh unclaimed credit, carrying the delivery contract it was sold under. It does not grant any new quota."
        confirmLabel="Return credit"
        onConfirm={onReturn}
        testId="return-credit"
        buttonClassName="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
      />
    </div>
  );
}

/* ── Column empty states — guidance, not dead ends ── */
const COLUMN_EMPTY_COPY = {
  fallback: {
    title: "No cards waiting for an expert",
    hint: "Cards land here when no assignment strategy matches a provider — a delivery manager holds them until one does.",
  },
  assigned: {
    title: "Nothing waiting to start",
    hint: "Tasks move here once a provider is assigned.",
  },
  in_progress: {
    title: "No work in flight",
    hint: "Tasks move here when their first stage starts.",
  },
  on_hold: {
    title: "Nothing is paused",
    hint: "Held tasks wait here until resumed.",
  },
  completed: {
    title: "Nothing completed yet",
    hint: "Finished tasks land here.",
  },
};

export function ColumnEmptyState({ columnKey }) {
  const copy = COLUMN_EMPTY_COPY[columnKey] || { title: "Nothing here yet", hint: "" };
  return (
    <div className="flex flex-col items-center justify-center h-32 px-3 text-center">
      <p className="text-xs font-medium text-gray-400">{copy.title}</p>
      {copy.hint && <p className="text-xs text-gray-300 mt-0.5">{copy.hint}</p>}
    </div>
  );
}

/* ── Loading ──────────────────────────────────────────────────────────────
   The dashboard's two loaders, both from the shared icon set:
     LoadingBlock   a section, list, panel or modal body whose data has not
                    arrived. Takes the place of the content, so the content's
                    "nothing here" copy can never stand in for "not loaded".
     InlineSpinner  inside a button, chip, badge or row — "this one thing is
                    working" without moving the layout.
   An empty state is drawn only once the data it describes has answered. */

export function LoadingBlock({ label, className = "py-10" }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex flex-col items-center justify-center text-center ${className}`}
    >
      <LoadingSvg className="h-8 w-8 text-primary" />
      {label && <p className="mt-2 text-xs text-gray-400">{label}</p>}
      {!label && <span className="sr-only">Loading</span>}
    </div>
  );
}

/* A count that has not arrived — a column or section header's number, which
   would otherwise read 0. A pulsing pill, not a spinner: it sits in a row of
   real counts and must hold their place. */
export function CountSkeleton() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-5 bg-gray-100 rounded animate-pulse flex-shrink-0"
    />
  );
}

export function InlineSpinner({ className = "h-3.5 w-3.5" }) {
  return (
    <span role="status" className="inline-flex items-center">
      <SpinnerIcon className={className} />
      <span className="sr-only">Loading</span>
    </span>
  );
}
